import {
  Avatar,
  Box,
  Container,
  Grid,
  IconButton,
  Stack,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DetailContainer } from 'components/terms/Containers';
import { useTitle } from 'utils/router';

import HeroSection from 'components/info/Hero';
import personIm from 'assets/images/lps/personimage5.webp';
import SaleemIm from 'assets/images/user/Saleem.png';
import JonathanIm from 'assets/images/user/Jonathan.png';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { Link } from 'react-router-dom';

const StyledIconButton = styled(IconButton)`
  & .MuiSvgIcon-root {
    fill: ${({ theme }) => theme.palette.grey[700]};
    &:hover {
      opacity: 0.8;
    }
  }
`;

export default function About(): JSX.Element {
  useTitle('About Us');
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <>
      <HeroSection
        title={'For freelancers who mean business.'}
        minHeight={isMobileScreen ? '260px' : '470px'}
        personImage={personIm}
      />
      <Box bgcolor="#F8F7F4">
        <Container maxWidth="lg">
          <DetailContainer>
            <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
              We became freelancers to be our own boss, set our schedule, and do work we care about. But that often gets
              lost in hours of scrolling job boards and marketplaces, competing with hundreds of others, and dealing
              with platforms that take large commissions or flood you with junk.
            </Typography>
            <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
              Sound familiar? Yeah, we’ve been there too.
            </Typography>
            <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
              That’s why we built Shoutt—not another marketplace. We’re the ultimate AI-powered gig aggregator
            </Typography>
            <ul style={{ margin: '0px', marginTop: '8px' }}>
              <li>
                <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
                  <b>All the best gigs in one place: </b> We bring you thousands of relevant freelance opportunities,
                  updated in real time, in one simple centralised place. No more bouncing between sites.
                </Typography>
              </li>
              <li style={{ marginTop: '8px' }}>
                <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
                  <b>Real-time alerts, daily emails, zero noise:</b> Get notified instantly when new gigs match your
                  filters, or get a personalised batch in your inbox every day. You set the filters, we do the heavy
                  lifting.
                </Typography>
              </li>
              <li style={{ marginTop: '8px' }}>
                <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
                  <b>No commissions, no middlemen: </b> You apply directly to clients on the original source. We’re not
                  here to take a slice of your earnings—just to help you grow and discover opportunities you could
                  easily miss.
                </Typography>
              </li>
              <li style={{ marginTop: '8px' }}>
                <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
                  <b>Only quality gigs: </b> We filter out lowball offers, vague briefs, and outdated listings. You see
                  only what’s worth your time—because time is precious.
                </Typography>
              </li>
              <li style={{ marginTop: '8px' }}>
                <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
                  <b>Built for solo professionals: </b> We’re here to help you build a sustainable solo business—on your
                  terms. Whether you’re freelancing part-time or full-time, or even building an agency, Shoutt supports
                  your freedom.
                </Typography>
              </li>
            </ul>
            <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
              And we’re just getting started. With smart personalisation, growth tools, and more powerful AI features on
              the way, Shoutt is here to make freelancing less about hustle and more about real progress.
            </Typography>
            <Typography variant={isMobileScreen ? 'h6' : 'h5'} mt={1.5}>
              Our Mission
            </Typography>
            <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
              We’re building the fastest way for freelancers to find meaningful work and secure their independence.
            </Typography>
            <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
              Shoutt replaces hours of scrolling with relevant opportunities delivered on a plate—so you can grow your
              business with clarity and control.
            </Typography>

            <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
              Next up: we’re going even further. Our goal is for Shoutt to help automate the entire process of finding
              and winning freelance work.
            </Typography>
            <Typography variant={isMobileScreen ? 'body2' : 'body1'}>
              We believe the future of work is freelance—and we’re here to power it.
            </Typography>

            <Typography variant={isMobileScreen ? 'h6' : 'h5'} mt={1} mb={isMobileScreen ? 0 : 1}>
              Our Founders
            </Typography>
            <Grid container>
              <Grid item xs={12} md={6} lg={5}>
                <Stack direction={'row'} spacing={2} mt={isMobileScreen ? 2 : undefined} alignItems={'center'}>
                  <Avatar
                    sx={{
                      width: isMobileScreen ? '90px' : '96px',
                      height: isMobileScreen ? '90px' : '96px',

                      mt: isMobileScreen ? 1.5 : undefined,
                      borderRadius: '12px',
                    }}
                    src={SaleemIm}
                    variant="square"
                  ></Avatar>
                  <Stack direction={'column'} pt={1}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color={'#101828'}
                      fontSize={isMobileScreen ? '20px' : undefined}
                    >
                      Saleem Yaqub
                    </Typography>
                    <Typography
                      variant={isMobileScreen ? 'body2' : 'body1'}
                      fontWeight={400}
                      color={'#475467'}
                      mt={0.5}
                    >
                      Co-Founder & CEO
                    </Typography>
                    <Link to="https://www.linkedin.com/in/saleemyaqub/" target="_blank">
                      <StyledIconButton sx={{ padding: 0 }}>
                        <LinkedInIcon
                          sx={{
                            height: isMobileScreen ? '24px' : '28px',
                            width: isMobileScreen ? '24px' : '28px',
                            mt: 0.5,
                            cursor: 'pointer',
                          }}
                        ></LinkedInIcon>
                      </StyledIconButton>
                    </Link>
                  </Stack>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6} lg={5}>
                <Stack direction={'row'} spacing={2} mt={isMobileScreen ? 2 : undefined} alignItems={'center'}>
                  <Avatar
                    sx={{
                      width: isMobileScreen ? '90px' : '96px',
                      height: isMobileScreen ? '90px' : '96px',
                      mt: isMobileScreen ? 1.5 : undefined,
                      borderRadius: '12px',
                    }}
                    src={JonathanIm}
                    variant="square"
                  ></Avatar>
                  <Stack direction={'column'} pt={1}>
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color={'#101828'}
                      fontSize={isMobileScreen ? '20px' : undefined}
                    >
                      Jonathan Eadie
                    </Typography>
                    <Typography
                      variant={isMobileScreen ? 'body2' : 'body1'}
                      fontWeight={400}
                      color={'#475467'}
                      mt={0.5}
                    >
                      Co-Founder & CTO
                    </Typography>
                    <Link to="https://www.linkedin.com/in/jonathan-eadie/" target="_blank">
                      <StyledIconButton sx={{ padding: 0 }}>
                        <LinkedInIcon
                          sx={{
                            height: isMobileScreen ? '24px' : '28px',
                            width: isMobileScreen ? '24px' : '28px',
                            mt: 0.5,
                            cursor: 'pointer',
                          }}
                        ></LinkedInIcon>
                      </StyledIconButton>
                    </Link>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </DetailContainer>
        </Container>
      </Box>
    </>
  );
}
